import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { getCountryFromLocale } from '@shared/utils/src/i18n';
import { fromCents } from '@shared/utils/src/price/money';
import { START_CSA_SESSION_TOKEN } from '../../auth';
import type { Cart } from '../../codegen/types';
import { DiscountCodeErrorCode } from '../../codegen/types';
import { FreeGiftConfirmationModalForPromoCode } from '../../globalComponents/freeGift/FreeGiftConfirmationModalForPromoCode';
import { FreeGiftOutOfStockModal } from '../../globalComponents/freeGift/FreeGiftOutOfStockModal';
import { FreeGiftConfirmationModal } from '../../globalComponents/freeGift/freeGiftConfirmationModal/FreeGiftConfirmationModal';
import { DiscountExpiredModal } from '../../globalComponents/modal';
import { useGetCartLazyQuery } from '../../graphqlDocument/cart/getCart.generated';
import { useAddCsaTokenMutation } from '../../graphqlDocument/csa/addCsaToken.generated';
import { useDisableBodyScroll, useIsConfirmationPage, useNRBASession } from '../../hooks';
import { GiftCardType } from '../../types/giftCard';
import { AttributeName } from '../../types/newrelic';
import {
  CartStorageKeys,
  createAnchorTagHref,
  getBasketPageLink,
  getCurrencySymbol,
  getItem,
  getNumberOfItems,
  removeItem,
  setCartLocalStorageItems,
} from '../../utils';
import { newRelicAvailabilityChecker, setNewRelicAttributes } from '../../utils/newrelic';
import { useLocaleContext } from '../localeContext';
import { useStaticContext } from '../staticContext';
import { useUserContext } from '../userContext';
import type { CartContextType } from './CartContextType';
import { CartContext } from './cartContext';

export function CartContextProvider({ children }: { children: ReactNode }) {
  const router = useRouter();

  const { locale } = useLocaleContext();
  const { storeData } = useStaticContext();
  const { productQuantityCap, basketValueCap, skuCap } = { ...storeData };
  const { customerLoading, isUserLoggedIn } = useUserContext();
  const isConfirmationPage = useIsConfirmationPage();

  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [cart, setCart] = useState<Cart | Record<string, never>>({});
  const [anonymousId, setAnonymousId] = useState<string>('');
  const [cartId, setCartId] = useState<string>('');
  const [orderValue, setOrderValue] = useState<number | null>(null);
  const [guid, setGuid] = useState<string>('');
  const [hasLastItemDeleted, setHasLastItemDeleted] = useState(false);
  const [checkoutSecurely, setCheckoutSecurely] = useState(false);
  const [isRetrievingIds, setIsRetrievingIds] = useState(true);
  const [shouldUpdateDeliveryOptions, setShouldUpdateDeliveryOptions] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isQuickBuyOpen, setIsQuickBuyOpen] = useState(false);
  const { total, currency: localeCurrency } = { ...cart };

  const basketTotal = fromCents(total) || '0';
  const currency = getCurrencySymbol(localeCurrency);
  const itemsInCart = getNumberOfItems(cart?.lineItems);
  const isMaxItemsReached = productQuantityCap ? itemsInCart > productQuantityCap : false;
  const isBasketValueExceeded = basketValueCap ? Number(basketTotal) > basketValueCap : false;
  const isLineItemsEmpty = cart?.lineItems?.length < 1;
  const isSkuCapReached = !!cart?.lineItems?.find(({ quantity }) => skuCap && quantity >= skuCap);
  const isSkuCapExceeded = !!cart?.lineItems?.find(({ quantity }) => skuCap && quantity > skuCap);
  const isCartEmpty = (!cart?.lineItems || cart?.lineItems?.length === 0) && !hasLastItemDeleted;

  // '/checkout' will cover global-E checkout, other URLs will cover Adyen checkout
  const shouldRecalculate = ['/checkout', '/basket', '/cart', '/checkout/delivery', '/checkout/payment'].includes(
    // `asPath` is the only `router`s property which is changing together with URL change.
    // However, it includes search query. We need to get rid of search query to be sure of clean URL
    router.asPath.split('?')[0],
  );

  const [addCsaTokenMutation] = useAddCsaTokenMutation();

  const updateCart: CartContextType['setCart'] = useCallback((incomingCart) => {
    const inactiveDiscountCodes = incomingCart?.errors
      ?.filter((error) => {
        if (error?.__typename === 'DiscountCodeError') {
          return [DiscountCodeErrorCode.NOT_VALID, DiscountCodeErrorCode.NOT_ACTIVE].includes(error?.errorCode);
        }
        return false;
      })
      .map<string>((error: any) => error.discountCode);

    const activeDiscountCodes = incomingCart?.discountCodes?.filter(
      (discount) => discount?.discountCode && !inactiveDiscountCodes?.includes(discount?.discountCode),
    );

    setCart({
      ...incomingCart,
      discountCodes: activeDiscountCodes,
      hasPhysicalGiftCard: incomingCart.lineItems?.some((item) => item.productTypeKey === GiftCardType.GIFT_CARD),
    });
  }, []);

  const nrbaContextHeader = useNRBASession();

  const [getCartQuery, { loading: isGetCartLoading, refetch: refetchCart, variables: cartInputVariables }] =
    useGetCartLazyQuery({
      ...nrbaContextHeader,
      onCompleted: ({ getCart: fetchedCart }) => {
        updateCart((fetchedCart || {}) as Cart);
        setCartId(fetchedCart?.id ?? '');
        setGuid(fetchedCart?.guid ?? '');
        setOrderValue(+fromCents(fetchedCart?.total));
        setAnonymousId(fetchedCart?.anonymousId ?? '');
        setCartLocalStorageItems(
          locale,
          fetchedCart?.id ?? '',
          fetchedCart?.anonymousId ?? '',
          fetchedCart?.guid ?? '',
        );
      },
      onError: (error) => {
        console.error('Error fetching cart data:', error);
        newRelicAvailabilityChecker(() => {
          window.newrelic.noticeError(new Error(`Error fetching cart data: ${error}`));
        });
      },
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    const fetchCart = async () => {
      setIsRetrievingIds(true);

      const cartIdInStorage = getItem(
        `${CartStorageKeys.CART_ID}_${getCountryFromLocale(locale)}`,
        true,
        'localStorage',
      );
      const cartAnonIdInStorage = getItem(
        `${CartStorageKeys.CART_ANON_ID}_${getCountryFromLocale(locale)}`,
        true,
        'localStorage',
      );
      const cartGuidInStorage = getItem(
        `${CartStorageKeys.GUID_ID}_${getCountryFromLocale(locale)}`,
        true,
        'localStorage',
      );

      const startCsaSessionToken = getItem(START_CSA_SESSION_TOKEN);

      if (cartIdInStorage) {
        setCartId(cartIdInStorage);
        setAnonymousId(cartAnonIdInStorage);
        setGuid(cartGuidInStorage);
      }

      if (isConfirmationPage) {
        setIsRetrievingIds(false);
      } else {
        await getCartQuery({
          variables: {
            input: {
              cartId: cartIdInStorage ?? '',
              anonymousId: cartAnonIdInStorage ?? '',
              locale,
              ...(shouldRecalculate && { recalculate: true }),
            },
          },
        });

        if (cartIdInStorage && startCsaSessionToken) {
          try {
            await addCsaTokenMutation({
              variables: {
                input: {
                  cartId: cartIdInStorage,
                  anonymousId: cartAnonIdInStorage ?? '',
                  csaToken: startCsaSessionToken,
                  locale,
                },
              },
            });
            removeItem(START_CSA_SESSION_TOKEN);
          } catch (e) {
            console.error(e);
          }
        }

        setIsRetrievingIds(false);
      }
    };

    if (customerLoading) return;

    fetchCart();
  }, [
    locale,
    getCartQuery,
    customerLoading,
    isConfirmationPage,
    addCsaTokenMutation,
    isUserLoggedIn,
    shouldRecalculate,
  ]);

  useDisableBodyScroll(checkoutSecurely);

  const isCheckoutFlow = getItem(CartStorageKeys.CHECKOUT_FLOW);

  const isMaxItemsReachedOrBasketValueExceeded = useCallback(() => {
    if ((!isCheckoutFlow && (isMaxItemsReached || isBasketValueExceeded || isSkuCapExceeded)) || isLineItemsEmpty) {
      window.location.href = createAnchorTagHref({ locale, path: getBasketPageLink(locale) });
    }
  }, [isCheckoutFlow, isBasketValueExceeded, isMaxItemsReached, locale, isLineItemsEmpty, isSkuCapExceeded]);

  // This wrapper function ensures refetch always uses the current cart IDs from state
  // instead of potentially stale IDs from the original query variables
  const refetchCartWithCurrentIds = useCallback(
    () =>
      refetchCart({
        input: {
          cartId,
          anonymousId,
          locale,
          ...(shouldRecalculate && { recalculate: true }),
        },
      }),
    [cartId, anonymousId, locale, refetchCart, shouldRecalculate],
  );

  const value = useMemo(
    () => ({
      cart,
      setCart: updateCart,
      refetchCart,
      refetchCartWithCurrentIds,
      showConfirmationMessage,
      setShowConfirmationMessage,
      anonymousId,
      setAnonymousId,
      cartId,
      setCartId,
      guid,
      setGuid,
      isCartLoading: isGetCartLoading || isRetrievingIds,
      hasLastItemDeleted,
      setHasLastItemDeleted,
      checkoutSecurely,
      setCheckoutSecurely,
      isCartEmpty,
      basketTotal,
      basketTotalCent: total,
      currency,
      itemsInCart,
      isMaxItemsReached,
      isBasketValueExceeded,
      isMaxItemsReachedOrBasketValueExceeded,
      isLineItemsEmpty,
      isSkuCapReached,
      isSkuCapExceeded,
      cartInputVariables,
      shouldUpdateDeliveryOptions,
      setShouldUpdateDeliveryOptions,
      isPopupOpen,
      setIsPopupOpen,
      isQuickBuyOpen,
      setIsQuickBuyOpen,
    }),
    [
      cart,
      updateCart,
      refetchCart,
      refetchCartWithCurrentIds,
      showConfirmationMessage,
      anonymousId,
      cartId,
      guid,
      isGetCartLoading,
      isRetrievingIds,
      hasLastItemDeleted,
      checkoutSecurely,
      isCartEmpty,
      basketTotal,
      total,
      currency,
      itemsInCart,
      isMaxItemsReached,
      isBasketValueExceeded,
      isMaxItemsReachedOrBasketValueExceeded,
      isLineItemsEmpty,
      isSkuCapReached,
      isSkuCapExceeded,
      cartInputVariables,
      shouldUpdateDeliveryOptions,
      isPopupOpen,
      setIsPopupOpen,
      isQuickBuyOpen,
      setIsQuickBuyOpen,
    ],
  );

  useEffect(() => {
    setNewRelicAttributes({
      [AttributeName.ORDER_ID]: cartId || null,
      [AttributeName.ORDER_VALUE]: orderValue,
      [AttributeName.ORDER_CART_ID]: anonymousId || null,
    });
  }, [orderValue, cartId, anonymousId]);

  return (
    <CartContext.Provider value={value}>
      {children}
      <DiscountExpiredModal />
      <FreeGiftConfirmationModalForPromoCode />
      <FreeGiftOutOfStockModal />
      <FreeGiftConfirmationModal />
    </CartContext.Provider>
  );
}
